import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ExternalLink, Megaphone } from 'lucide-react';

interface Feature {
  id: number;
  title: string;
  description: string;
  learnMoreUrl: string | null;
  sortOrder: number;
}

interface ProductUpdate {
  id: number;
  version: string;
  releaseDate: string;
  headline: string;
  heroImageUrl: string | null;
  heroDescription: string;
  features: Feature[];
}

export default function ProductUpdates() {
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const { data: updates = [], isLoading } = useQuery<ProductUpdate[]>({
    queryKey: ['/api/product-updates'],
    queryFn: () => apiRequest('GET', '/api/product-updates'),
  });

  const selectedUpdate = selectedId ? updates.find((u) => u.id === selectedId) : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600" />
      </div>
    );
  }

  // Detail view
  if (selectedUpdate) {
    const releaseDateFormatted = new Date(selectedUpdate.releaseDate).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });

    return (
      <div className="min-h-full bg-slate-50 dark:bg-gray-950">
        <div className="mx-auto w-full max-w-3xl px-6 py-10">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedId(null)}
            className="mb-6 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to all updates
          </Button>

          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <Badge variant="outline" className="text-xs font-mono">
                v{selectedUpdate.version}
              </Badge>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {releaseDateFormatted}
              </span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {selectedUpdate.headline}
            </h1>
          </div>

          {/* Hero image */}
          {selectedUpdate.heroImageUrl && (
            <div className="mb-6 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm">
              <img
                src={selectedUpdate.heroImageUrl}
                alt={selectedUpdate.headline}
                className="w-full h-auto max-h-80 object-cover"
              />
            </div>
          )}

          {/* Hero description */}
          <p className="text-base leading-relaxed text-gray-700 dark:text-gray-300 mb-8">
            {selectedUpdate.heroDescription}
          </p>

          {/* Feature Highlights */}
          {selectedUpdate.features.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                What's New
              </h2>
              {selectedUpdate.features.map((feature) => (
                <Card key={feature.id} className="bg-white dark:bg-gray-900">
                  <CardContent className="p-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {feature.description}
                    </p>
                    {feature.learnMoreUrl && (
                      <a
                        href={feature.learnMoreUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-2 text-sm font-medium text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300"
                      >
                        Learn more
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="min-h-full bg-slate-50 dark:bg-gray-950">
      <div className="mx-auto w-full max-w-3xl px-6 py-10">
        <header className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Megaphone className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              Product Updates
            </h1>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Stay up to date with the latest features and improvements.
          </p>
        </header>

        {updates.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <Megaphone className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">
                No updates yet
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Product updates will appear here when new features are released.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 top-3 bottom-3 w-px bg-gray-200 dark:bg-gray-700" />

            <div className="space-y-4">
              {updates.map((update) => {
                const releaseDateFormatted = new Date(update.releaseDate).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  timeZone: 'UTC',
                });

                return (
                  <div key={update.id} className="relative pl-10">
                    {/* Timeline dot */}
                    <div className="absolute left-2.5 top-5 w-3 h-3 rounded-full bg-orange-500 border-2 border-white dark:border-gray-950 z-10" />

                    <Card
                      className="hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => setSelectedId(update.id)}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-center gap-3 mb-1">
                          <Badge variant="outline" className="text-xs font-mono">
                            v{update.version}
                          </Badge>
                          <span className="text-sm text-gray-400 dark:text-gray-500">
                            {releaseDateFormatted}
                          </span>
                        </div>
                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                          {update.headline}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                          {update.heroDescription}
                        </p>
                        <div className="mt-2 text-xs text-gray-400">
                          {update.features.length} feature highlight{update.features.length !== 1 ? 's' : ''}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
